import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  type ActionMeta,
  AsyncSelect,
  type StylesConfig,
  components,
} from '@databases/atlaskit/Select';
import { messages } from '@databases/intl/messages';
import { autoCompleteLabel } from '@databases/utils/confluence/contentLabel';
import debounce from 'debounce-promise';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import {
  baseSelectStyleConfig,
  customPlaceholderAndControlSpacingStyling,
} from '../popover-edit-common';

export interface PageLabelsSelectOption {
  label: string;
  value: string;
}

const MultiValueRemove = (props: any) => {
  return (
    <components.MultiValueRemove {...props}>
      <CrossIcon label="" />
    </components.MultiValueRemove>
  );
};

const ClearIndicator = (props: any) => {
  const {
    children = <CrossIcon label="" />,
    innerProps: { ref, ...innerProps },
  } = props;
  return (
    <Box {...innerProps} ref={ref}>
      {children}
    </Box>
  );
};

const styles: StylesConfig<PageLabelsSelectOption, boolean> = {
  menu: (base) => ({
    ...base,
    width: '100%',
    minWidth: '260px',
    position: 'relative',
    margin: '0',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0',
    width: '100%',
  }),
  input: (base) => ({
    ...base,
  }),
  multiValue: (base) => ({
    ...base,
    border: `1px solid ${token('color.border', '#091E4224')}`,
    borderRadius: token('radius.small', '3px'),
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: token('color.text', '#172B4D'),
  }),
  multiValueRemove: (base) => ({
    ...base,
    backgroundColor: 'transparent',
    borderRadius: token('radius.small', '3px'),
    ':hover': {
      backgroundColor: 'transparent',
    },
  }),
};

function getOptionLabel(option: PageLabelsSelectOption): string {
  return option.label;
}

function getOptionValue(option: PageLabelsSelectOption): string {
  return option.value;
}

export interface PageLabelsSelectProps {
  value: string[];
  onChange: (value: string[], action: ActionMeta<PageLabelsSelectOption>) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isSeamless?: boolean;
  isInvalid?: boolean;
  isLoading?: boolean;
  onInputChange?: () => void;
  linkedPageContentId: string | undefined;
}

export const getMappedPageLabels = (value: string[]) =>
  value.map((v) => ({ label: v, value: v }));

/**
 * Renders a multi select for page labels with autocomplete.
 */
const PageLabelsSelect: FunctionComponent<PageLabelsSelectProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isInvalid,
  isSeamless,
  isLoading,
  onInputChange,
  linkedPageContentId,
}) => {
  const { formatMessage } = useIntl();

  const [inputValue, setInputValue] = useState('');

  const handleChange = useCallback(
    (
      value: ReadonlyArray<PageLabelsSelectOption>,
      action: ActionMeta<PageLabelsSelectOption>
    ) => {
      onChange(
        value.map((o) => o.value),
        action
      );
    },
    [onChange]
  );

  const loadOptions = useCallback(async (): Promise<PageLabelsSelectOption[]> => {
    const query = inputValue.trim();
    const lowerQuery = query.toLowerCase();
    const labels = await autoCompleteLabel(query, linkedPageContentId);
    if (!query || labels.some((label) => label.toLowerCase() == lowerQuery)) {
      return labels.map((v) => ({ label: v, value: v }));
    } else {
      return [
        {
          label: formatMessage(
            messages['field-settings.type-page-label-create-label-text'],
            { query }
          ),
          value: query,
        },
        ...labels.map((v) => ({ label: v, value: v })),
      ];
    }
  }, [formatMessage, inputValue, linkedPageContentId]);

  const debouncedLoadOptions = useMemo(() => debounce(loadOptions, 400), [loadOptions]);

  const mappedValue: PageLabelsSelectOption[] = useMemo(
    () => getMappedPageLabels(value),
    [value]
  );

  const handleSetInputValue = useCallback(
    (newValue: string) => {
      setInputValue(newValue.replaceAll(' ', '-'));
      onInputChange?.();
    },
    [onInputChange]
  );

  return (
    <AsyncSelect<PageLabelsSelectOption, true>
      inputValue={inputValue}
      onInputChange={handleSetInputValue}
      loadOptions={debouncedLoadOptions}
      value={mappedValue}
      onChange={handleChange}
      isClearable
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      isMulti
      isSeamless={isSeamless}
      isInvalid={isInvalid}
      isLoading={isLoading}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      defaultMenuIsOpen={autoFocus && !mappedValue.length}
      styles={
        {
          ...baseSelectStyleConfig,
          ...styles,
          ...customPlaceholderAndControlSpacingStyling,
        } as StylesConfig<PageLabelsSelectOption, boolean>
      }
      components={{ ClearIndicator, MultiValueRemove }}
      placeholder={formatMessage(
        messages['page-labels-picker-popover.search-create-text']
      )}
    />
  );
};

export default PageLabelsSelect;
